
import React, { useState, useEffect } from 'react';
import { User, UserProfileType, ViewState } from '../types';
import { LogOut, Zap, LayoutDashboard, PlusCircle, Book, Settings, Sun, Moon, GraduationCap, Clock, ExternalLink, Users } from 'lucide-react';

interface LayoutProps {
  children: React.ReactNode;
  user: User | null;
  currentView: ViewState;
  onNavigate: (view: ViewState) => void;
  onLogout: () => void;
}

const Layout: React.FC<LayoutProps> = ({ children, user, currentView, onNavigate, onLogout }) => {
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    return (localStorage.getItem('chroma-theme') as 'light' | 'dark') || 'dark';
  });

  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('chroma-theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };
  
  const NavItem = ({ view, label, icon: Icon }: { view: ViewState, label: string, icon: any }) => {
    const isActive = currentView === view;
    return (
      <button
        onClick={() => onNavigate(view)}
        className={`flex flex-col sm:flex-row items-center gap-1 sm:gap-2 px-3 py-2 rounded-xl transition-all text-[10px] sm:text-xs font-bold uppercase tracking-widest ${
          isActive 
            ? 'bg-purple-800 text-white shadow-md' 
            : 'text-chroma-muted hover:text-chroma-accent hover:bg-black/5 dark:hover:bg-white/5'
        }`}
      >
        <Icon size={16} className={isActive ? 'text-chroma-accent' : ''} />
        <span className="leading-none">{label}</span>
      </button>
    );
  };

  const hasPatientAccess = user && [UserProfileType.PRATA, UserProfileType.OURO, UserProfileType.MASTER].includes(user.profile);

  return (
    <div className="min-h-screen flex flex-col font-sans transition-colors duration-300 bg-chroma-dark text-chroma-text">
      <header className="bg-chroma-card border-b border-purple-900/10 dark:border-purple-900/50 shadow-lg sticky top-0 z-50 no-print">
        <div className="max-w-6xl mx-auto px-4 py-3 flex justify-between items-center gap-4">
          
          <div 
            className="flex items-center gap-3 cursor-pointer shrink-0" 
            onClick={() => user && onNavigate('DASHBOARD')}
          >
            <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-chroma-accent to-purple-600 flex items-center justify-center shadow-lg">
              <Zap size={22} className="text-white fill-white" />
            </div>
            <div className="hidden lg:block">
              <h1 className="text-base font-bold leading-tight tracking-tight text-chroma-text uppercase flex items-center">
                Cromoterapia <span className="text-chroma-accent ml-1">Aplicada</span>
                <span className="ml-1 text-[10px] bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-300 px-1.5 py-0.5 rounded font-black align-top">V2.0</span>
              </h1>
            </div>
          </div>

          {user && (
            <nav className="flex items-center gap-1 sm:gap-3 bg-purple-100/50 dark:bg-black/30 p-1.5 rounded-2xl border border-purple-200 dark:border-white/5 overflow-x-auto no-scrollbar">
              <NavItem view="DASHBOARD" label="Início" icon={LayoutDashboard} />
              <NavItem view="FORM" label="Novo" icon={PlusCircle} />
              {hasPatientAccess && <NavItem view="PATIENTS" label="Pacientes" icon={Users} />}
              <NavItem view="HISTORY" label="Histórico" icon={Clock} />
              <NavItem view="LIBRARY" label="Guia" icon={Book} />
              
              {user.profile === UserProfileType.MASTER && (
                <div className="border-l border-purple-300 dark:border-white/10 pl-1 sm:ml-1">
                   <NavItem view="ADMIN" label="Admin" icon={Settings} />
                </div>
              )}
            </nav>
          )}
          
          <div className="flex items-center gap-1 sm:gap-3 shrink-0">
            <button 
              onClick={toggleTheme}
              className="p-2.5 rounded-xl hover:bg-purple-100 dark:hover:bg-white/10 transition-all text-chroma-muted hover:text-chroma-accent"
            >
              {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            
            {user && (
              <button 
                onClick={onLogout}
                className="p-2.5 rounded-xl hover:bg-red-100 dark:hover:bg-red-900/20 transition-colors text-chroma-muted hover:text-red-500"
              >
                <LogOut size={18} />
              </button>
            )}
          </div>
        </div>
      </header>
      
      <main className="flex-1 w-full max-w-4xl mx-auto p-4 md:p-8 animate-fade-in">
        {children}
      </main>

      {user && (
        <div className="no-print w-full max-w-4xl mx-auto px-4 mb-8">
          <div className="bg-gradient-to-r from-purple-900/60 to-indigo-900/60 border border-purple-500/30 rounded-3xl p-6 flex flex-col sm:flex-row items-center justify-between gap-6 shadow-xl">
             <div className="flex items-center gap-5 text-center sm:text-left">
                <div className="p-3 bg-purple-500/20 rounded-2xl text-chroma-accent">
                   <GraduationCap size={32} />
                </div>
                <div>
                   <h4 className="text-white font-bold text-lg">Quer se tornar um Especialista?</h4>
                   <p className="text-purple-200/70 text-sm font-medium">Aprofunde seus conhecimentos no Curso Completo.</p>
                </div>
             </div>
             <a 
               href="https://caputoacademia.com.br/venda_cromoterapia" 
               target="_blank" 
               rel="noopener noreferrer"
               className="w-full sm:w-auto px-8 py-3 bg-gradient-to-r from-yellow-500 to-orange-600 text-white font-black rounded-2xl shadow-lg hover:shadow-orange-500/30 transition-all flex items-center justify-center gap-2 uppercase tracking-widest text-xs"
             >
                Ver Curso Completo
                <ExternalLink size={16} />
             </a>
          </div>
        </div>
      )}

      <footer className="py-12 text-center text-chroma-muted text-xs border-t border-purple-900/5 dark:border-purple-900/20 bg-purple-50 dark:bg-[#0b0314] no-print">
        <div className="flex flex-col items-center gap-4">
          <div className="flex items-center gap-2 opacity-30 grayscale">
            <Zap size={16} className="text-chroma-accent" />
            <span className="font-bold tracking-[0.3em] uppercase">JOÃO CAPUTO V2.0</span>
          </div>
          <p>© {new Date().getFullYear()} Cromoterapia Aplicada. Todos os direitos reservados.</p>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
